/* ═══════════════════════════════════════════════════════════════
   useConfirmDeleteTask — the ONE right-click-menu delete confirmation,
   shared by the List row menu (TasksTable) and Board card menu
   (TasksBoard). Same title/description/danger-styling as the panel's own
   Delete (TaskSheet.tsx ~:393-401) so a task is never deleted from a
   context menu without the kit ConfirmDialog (DESIGN.md §4).
   ═══════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { Task } from '../../../shared/types';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import type { TasksData } from './useTasksData';

export interface UseConfirmDeleteTask {
  /** Opens the confirm dialog for this task — call from a row/card menu's
   *  onDelete instead of calling bulkDeleteTasks directly. */
  requestDelete: (task: Task) => void;
  /** Render this once, anywhere in the component tree. */
  confirmDeleteDialog: ReactElement;
}

export function useConfirmDeleteTask(data: TasksData): UseConfirmDeleteTask {
  const [pending, setPending] = useState<Task | null>(null);
  const [busy, setBusy] = useState(false);

  function requestDelete(task: Task) {
    setPending(task);
  }

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    try {
      await data.bulkDeleteTasks([pending]);
      setPending(null);
    } finally {
      setBusy(false);
    }
  }

  const confirmDeleteDialog = (
    <ConfirmDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open && !busy) setPending(null);
      }}
      title="Delete this task?"
      description="Delete this task and all subtasks?"
      confirmLabel="Delete"
      destructive
      busy={busy}
      onConfirm={() => void confirm()}
    />
  );

  return { requestDelete, confirmDeleteDialog };
}
